import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useBlocker } from 'react-router';
import { useAuthStore } from '@indipia/shared';
import {
  Input,
  KeywordForm,
  Button,
  StatusHint,
  Segmented,
  Select,
  Modal,
  CollapsibleSection,
  CanvasPhotoRow,
  CanvasTextRow,
  Page,
  PageScroll,
  PageHeader,
} from '@indipia/ui-core';
import { portfolioApi } from '../api/portfolioApi';
import { PhotoPicker } from '../components/PhotoPicker';
import { PhotoSelectGrid, SelectedStrip } from '../components/PhotoSelectGrid';
import { ThumbImg } from '../components/ThumbImg';
import type {
  Block,
  BlockDocument,
  CompanyInfo,
  ProjectUpsertRequest,
  SelectedPhoto,
  SiteOption,
} from '../types/portfolio.types';

const isPhoto = (b: Block) => b.type === 'photo';
const isText = (b: Block) => b.type === 'text';

function blockPhotos(blocks: Block[]): SelectedPhoto[] {
  return blocks
    .filter(isPhoto)
    .flatMap((b) => (b.photos ?? []).map((p) => ({ photoId: p.photoId, url: p.url })));
}

/** 블록에 담긴 모든 문단 텍스트를 이어붙인다(스타일 변경 시 병합용). */
function allText(bs: Block[]): string {
  return bs
    .filter(isText)
    .map((t) => (t.text ?? '').trim())
    .filter(Boolean)
    .join('\n\n');
}

/**
 * 사진 블록에 고유 번호(bid)를 부여한다. 이미 있는 bid는 보존하고 없는 것만 채운다.
 * 순서를 바꿔도 블록이 자기 번호를 그대로 들고 다녀 "무엇이 어디로 갔는지" 식별된다.
 */
function withBids(bs: Block[]): Block[] {
  let max = 0;
  bs.forEach((b) => {
    if (isPhoto(b) && typeof b.bid === 'number') max = Math.max(max, b.bid);
  });
  return bs.map((b) => (isPhoto(b) && b.bid == null ? { ...b, bid: ++max } : b));
}

const PRESETS = [
  { label: '기본형', value: 'basic' },
  { label: '갤러리', value: 'gallery' },
  { label: '스토리', value: 'story' },
];

/** 문단 표시기 항목 명칭 — 사진+설명 한 덩어리를 부르는 말. 후보: 구역·묶음·단락·블록. 여기만 바꾸면 전역 반영. */
// 캔버스 좌우 sticky 컬럼의 최대 높이.
// 스크롤포트는 PageScroll 이지만 그 높이를 CSS 로 참조할 방법이 없어 뷰포트에서 역산한다.
// 매직넘버(구 `calc(100vh - 96px)`) 대신 토큰으로 표현해 TopBar 높이가 바뀌어도 따라간다.
// ponytail: 하단 액션 바 높이(약 61)는 spacing-11(80)에 근사로 포함시켰다.
//           정확히 맞춰야 할 만큼 어긋나면 ResizeObserver 로 PageScroll 높이를 재는 쪽으로 올린다.
const STICKY_COL_MAX = 'calc(100dvh - var(--layout-topbar-height) - var(--spacing-11))';

const UNIT_LABEL = '구역';

/** 스타일 예시(? 도움말 모달) — 예시 이미지는 shell-web public/portfolio 정적 에셋 */
const PRESET_EXAMPLES = [
  { label: '기본형', img: '/portfolio/preset-basic.svg', desc: '사진 1·2·2·1 배치가 반복됩니다. 사진 줄마다 바로 뒤에 문단(설명)이 붙습니다.' },
  { label: '갤러리', img: '/portfolio/preset-gallery.svg', desc: '사진을 3장씩 촘촘히 먼저 보여주고, 맨 아래에 문단(설명) 하나가 붙습니다.' },
  { label: '스토리', img: '/portfolio/preset-story.svg', desc: '사진을 한 장씩 크게, 사진마다 문단(설명)을 답니다.' },
];

/**
 * 문단 모드 — 스타일이 결정한다(§8-2).
 *  - perRow: 사진 줄마다 문단 1개(기본형·스토리)
 *  - trailing: 사진들 뒤에 문단 1개만(갤러리)
 */
type TextMode = 'perRow' | 'trailing';
const modeOf = (preset: string): TextMode => (preset === 'gallery' ? 'trailing' : 'perRow');

/** 프리셋 자동 묶음 — 사진 블록만 만든다(문단은 withParagraphs가 붙인다). §8-2 */
function buildBlocks(photos: SelectedPhoto[], preset: string): Block[] {
  const mk = (ps: SelectedPhoto[]): Block => ({
    type: 'photo',
    columns: ps.length,
    photos: ps.map((p) => ({ photoId: p.photoId, url: p.url })),
  });
  if (preset === 'gallery') {
    const out: Block[] = [];
    for (let i = 0; i < photos.length; i += 3) out.push(mk(photos.slice(i, i + 3)));
    return out;
  }
  if (preset === 'basic') {
    // 컬럼 1·2·2·1 반복
    const pattern = [1, 2, 2, 1];
    const out: Block[] = [];
    let i = 0;
    let k = 0;
    while (i < photos.length) {
      const c = pattern[k % pattern.length];
      out.push(mk(photos.slice(i, i + c)));
      i += c;
      k++;
    }
    return out;
  }
  // story
  return photos.map((p) => mk([p]));
}

/** 새로 만든 사진 블록에 문단을 붙인다(빈 문단, seed는 첫/유일 문단에 삽입). */
function withParagraphs(photoBlocks: Block[], mode: TextMode, seed = ''): Block[] {
  if (mode === 'trailing') {
    if (photoBlocks.length === 0) return withBids(seed ? [{ type: 'text', text: seed }] : []);
    return withBids([...photoBlocks, { type: 'text', text: seed }]);
  }
  const out: Block[] = [];
  photoBlocks.forEach((pb) => {
    out.push(pb);
    out.push({ type: 'text', text: '' });
  });
  if (seed && out.length) {
    const fi = out.findIndex(isText);
    if (fi >= 0) out[fi] = { type: 'text', text: seed };
  }
  return withBids(out);
}

/** 로드·사진증감 시 문단 재정규화 — 기존 문단 텍스트·고유 번호는 보존한다. */
function normalizeParagraphs(bs: Block[], mode: TextMode): Block[] {
  const photos = bs.filter(isPhoto);
  if (mode === 'trailing') {
    const merged = allText(bs);
    if (photos.length === 0) return withBids(merged ? [{ type: 'text', text: merged }] : []);
    return withBids([...photos, { type: 'text', text: merged }]);
  }
  const out: Block[] = [];
  for (let i = 0; i < bs.length; i++) {
    const b = bs[i];
    if (!isPhoto(b)) continue;
    out.push(b);
    const next = bs[i + 1];
    out.push(next && isText(next) ? next : { type: 'text', text: '' });
  }
  return withBids(out);
}

/** 저장된 문서에 preset이 없을 때(구 데이터) 문단 구조로 스타일을 추정한다. */
function inferPreset(bs: Block[]): string {
  const photos = bs.filter(isPhoto).length;
  const texts = bs.filter(isText).length;
  return photos > 1 && texts <= 1 ? 'gallery' : 'basic';
}

function previewText(t?: string): string {
  const s = (t ?? '').trim();
  if (!s) return '';
  return s.length > 16 ? s.slice(0, 16) + '…' : s;
}

/**
 * 시공사례 작성.
 *  - 신규: 'select'(전체화면 고르기) → 'canvas'
 *  - canvas = 3분할. 좌=문단 표시기(블록 목록 + ▲▼ 순서), 중=WYSIWYG 캔버스, 우=미배치 사진 팔레트.
 *    팔레트→빈 슬롯 드래그로 배치, 사진 ×로 빼면 팔레트 복귀. 순서 변경은 좌측 ▲▼로만. blocks 가 본문의 진실.
 */
export function ProjectWritePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('projectId');
  const projectId = editId ? Number(editId) : null;

  const [view, setView] = useState<'select' | 'canvas'>(projectId != null ? 'canvas' : 'select');
  const [title, setTitle] = useState('');
  const [locationText, setLocationText] = useState('');
  const [pyeong, setPyeong] = useState('');
  const [durationDays, setDurationDays] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [allSelected, setAllSelected] = useState<SelectedPhoto[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loading, setLoading] = useState(projectId != null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selMap, setSelMap] = useState<Map<number, SelectedPhoto>>(new Map());
  const [preset, setPreset] = useState('basic');
  const [dragPhotoId, setDragPhotoId] = useState<number | null>(null);
  const [dragUnitIdx, setDragUnitIdx] = useState<number | null>(null);

  // 현장 기반 진입 — 현장 선택 시 위치(단지명)·평수 자동채움
  const [sites, setSites] = useState<SiteOption[]>([]);
  // 내 현장 목록을 받아왔는지 — 0건 안내를 로딩 중에 띄우지 않기 위함
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const myId = useAuthStore((st) => st.user?.memberId ?? null);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [otherOpen, setOtherOpen] = useState(false); // "다른 현장 포함" — 기본 접힘

  // 에디터 상단 접이식 섹션
  const [company, setCompany] = useState<CompanyInfo | null>(null);
  const [companyOpen, setCompanyOpen] = useState(false); // 회사정보 — 기본 접힘
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagOpen, setHashtagOpen] = useState(true); // 해시태그 — 기본 펼침

  // 저장 안 한 변경 감지(이탈 경고용) — 캔버스 진입 시점 스냅샷 대비 변경 여부
  const [dirty, setDirty] = useState(false);
  const baselineRef = useRef<string | null>(null);
  const bypassBlockRef = useRef(false); // 저장·취소의 정상 이동은 가드 통과

  const textMode = modeOf(preset);

  useEffect(() => {
    if (projectId == null) return;
    const controller = new AbortController();
    setLoading(true);
    portfolioApi
      .getProject(projectId, controller.signal)
      .then((res) => {
        const p = res.data.data;
        setTitle(p.projectTitle ?? '');
        setSiteId(p.siteId ?? null);
        setHashtags((p.tags ?? []).map((t) => t.tagValue));
        setLocationText(p.locationText ?? '');
        // projectPyeong은 DB 타입 변경(String→Integer)으로 number — 입력창은 여전히 자유 텍스트라 문자열로 되돌려 채운다.
        setPyeong(p.projectPyeong != null ? String(p.projectPyeong) : '');
        setDurationDays(p.durationDays != null ? String(p.durationDays) : '');
        setBudgetRange(p.budgetRange ?? '');
        const loaded = p.content?.blocks ?? [];
        const pst = p.content?.preset ?? inferPreset(loaded);
        setPreset(pst);
        const bs = normalizeParagraphs(loaded, modeOf(pst));
        setBlocks(bs);
        setAllSelected(blockPhotos(bs));
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setError('사례를 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [projectId]);

  // 신규 작성 진입 — 현장 목록 로드(현장 선택 드롭다운).
  // 시공사례 작성은 항상 '내 현장'(로그인 회원이 현장 담당자 또는 관리자인 현장)만 — 사장님 포함, 체크박스 없이 고정.
  // 로그인 정보가 아직 없으면 사업장 전체 목록이 잠깐 보이지 않도록 호출하지 않고 기다린다.
  useEffect(() => {
    if (projectId != null || myId == null) return;
    const controller = new AbortController();
    setSitesLoaded(false);
    portfolioApi
      .getSites(myId, controller.signal)
      .then((res) => { setSites(res.data.data.items ?? []); setSitesLoaded(true); })
      .catch(() => {});
    return () => controller.abort();
  }, [projectId, myId]);

  // 회사정보 로드(에디터 상단 섹션 — 읽기 전용)
  useEffect(() => {
    const controller = new AbortController();
    portfolioApi
      .getCompany(controller.signal)
      .then((res) => setCompany(res.data.data))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // 저장 안 한 변경 감지 — 캔버스 진입 후 스냅샷(제목·기본정보·해시태그·본문) 대비 변경 시 dirty
  const dirtySnapshot = JSON.stringify({ title, locationText, pyeong, durationDays, budgetRange, hashtags, blocks });
  useEffect(() => {
    if (view !== 'canvas' || loading) return;
    if (baselineRef.current === null) {
      baselineRef.current = dirtySnapshot; // 진입 기준선 — 이후 변경만 dirty
      return;
    }
    setDirty(dirtySnapshot !== baselineRef.current);
  }, [dirtySnapshot, view, loading]);

  // 탭 닫기·새로고침·외부 이동 시 경고(브라우저 기본 다이얼로그)
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // 앱 내 라우팅 이동(LNB 등)도 미저장 변경 있으면 확인 후 진행(데이터 라우터 useBlocker)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !bypassBlockRef.current && dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('저장하지 않은 변경사항이 있습니다. 나가시겠어요?')) blocker.proceed?.();
    else blocker.reset?.();
  }, [blocker]);

  // 취소(초기화) — 미저장 변경 있으면 확인. 신규는 사진 선택으로 되돌리고, 편집은 목록으로.
  function onCancel() {
    if (dirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 나가시겠어요?')) return;
    if (projectId == null) {
      baselineRef.current = null;
      setDirty(false);
      setView('select');
    } else {
      bypassBlockRef.current = true; // 확인 완료 후 이동은 가드 통과(중복 확인 방지)
      navigate('/agents/portfolio/projects');
    }
  }

  // 현장 선택 → 위치(단지명까지·프라이버시)·평수 자동채움. SITE_ID 는 원장 귀속.
  async function onSelectSite(id: number | null) {
    setSiteId(id);
    if (id == null) return;
    try {
      const res = await portfolioApi.getSiteDetail(id);
      const s = res.data.data;
      setLocationText(s.bldgName || s.siteName || '');
      if (s.sitePyeong != null) setPyeong(String(s.sitePyeong));
    } catch {
      /* 자동채움 실패는 무시 — 수동 입력 */
    }
  }

  const usedIds = new Set(
    blocks.flatMap((b) => (isPhoto(b) ? (b.photos ?? []).map((p) => p.photoId) : [])),
  );
  const palette = allSelected.filter((p) => !usedIds.has(p.photoId));

  // ── 캔버스 조작 ──
  function addPhotoToRow(rowIdx: number, photoId: number) {
    const photo = allSelected.find((p) => p.photoId === photoId);
    if (!photo) return;
    setBlocks((bs) =>
      bs.map((b, i) => {
        if (i !== rowIdx || b.type !== 'photo') return b;
        const cols = b.columns ?? 1;
        const ps = b.photos ?? [];
        if (ps.length >= cols) return b;
        return { ...b, photos: [...ps, { photoId: photo.photoId, url: photo.url }] };
      }),
    );
  }
  function removePhotoFromRow(rowIdx: number, photoId: number) {
    setBlocks((bs) =>
      bs.map((b, i) =>
        i === rowIdx && b.type === 'photo'
          ? { ...b, photos: (b.photos ?? []).filter((p) => p.photoId !== photoId) }
          : b,
      ),
    );
  }
  function changeColumns(rowIdx: number, delta: number) {
    setBlocks((bs) =>
      bs.map((b, i) => {
        if (i !== rowIdx || b.type !== 'photo') return b;
        const cols = Math.max(1, Math.min(3, (b.columns ?? 1) + delta));
        return { ...b, columns: cols, photos: (b.photos ?? []).slice(0, cols) };
      }),
    );
  }
  function setCaption(rowIdx: number, caption: string) {
    setBlocks((bs) => bs.map((b, i) => (i === rowIdx ? { ...b, caption } : b)));
  }
  function setText(rowIdx: number, text: string) {
    setBlocks((bs) => bs.map((b, i) => (i === rowIdx ? { ...b, text } : b)));
  }
  // 사진 줄 삭제 — perRow는 딸린 문단도 함께, trailing은 사진만(끝 문단 유지)
  function removeRow(rowIdx: number) {
    setBlocks((bs) => {
      const nb = [...bs];
      if (textMode === 'perRow') {
        const count = nb[rowIdx + 1]?.type === 'text' ? 2 : 1;
        nb.splice(rowIdx, count);
      } else {
        nb.splice(rowIdx, 1);
      }
      return nb;
    });
  }

  // 좌측 문단 표시기 드래그 재정렬 — 사진 줄(구역) 순서 이동.
  // perRow는 (사진+문단) 쌍, trailing은 사진만(끝 문단 고정). from 을 to 위치로 이동.
  function reorderUnit(from: number, to: number) {
    if (from === to) return;
    setBlocks((bs) => {
      if (textMode === 'trailing') {
        const photos = bs.filter(isPhoto);
        const texts = bs.filter(isText);
        if (from < 0 || from >= photos.length || to < 0 || to >= photos.length) return bs;
        const np = [...photos];
        const [moved] = np.splice(from, 1);
        np.splice(to, 0, moved);
        return [...np, ...texts];
      }
      const pairs: Block[][] = [];
      for (let i = 0; i < bs.length; i++) {
        if (!isPhoto(bs[i])) continue;
        const next = bs[i + 1];
        pairs.push([bs[i], next && isText(next) ? next : { type: 'text', text: '' }]);
      }
      if (from < 0 || from >= pairs.length || to < 0 || to >= pairs.length) return bs;
      const np = [...pairs];
      const [moved] = np.splice(from, 1);
      np.splice(to, 0, moved);
      return np.flat();
    });
  }

  function toggleSel(
    photoId: number,
    url: string,
    meta?: { spaceTag?: string | null; siteId?: number | null; siteName?: string | null },
  ) {
    setSelMap((prev) => {
      const next = new Map(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.set(photoId, { photoId, url, spaceTag: meta?.spaceTag, siteId: meta?.siteId, siteName: meta?.siteName });
      return next;
    });
  }
  function startCanvas() {
    const chosen = [...selMap.values()];
    setAllSelected(chosen);
    setBlocks(withParagraphs(buildBlocks(chosen, preset), modeOf(preset)));
    // 해시태그 기본값 = 선택 사진 공간태그 + 위치(단지명) + 평수 (중복 제거)
    const spaceTags = chosen.map((p) => (p.spaceTag ?? '').trim()).filter(Boolean);
    const seed = [...new Set([...spaceTags, locationText.trim(), pyeong.trim() ? `${pyeong.trim()}평` : ''])].filter(
      Boolean,
    );
    setHashtags(seed);
    setView('canvas');
  }

  // 편집화면 스타일(프리셋) 실시간 재배치.
  // 현재 배치된 사진을 현재 순서 그대로 프리셋으로 재그룹(팔레트 사진은 되돌리지 않음).
  // 캡션은 초기화(새 블록), 입력한 문단 텍스트는 모두 이어붙여 하나로 합친다.
  function applyPreset(next: string) {
    setPreset(next);
    setBlocks((bs) => withParagraphs(buildBlocks(blockPhotos(bs), next), modeOf(next), allText(bs)));
  }

  // 모달: 전체 선택 갱신 — 빠진 사진은 블록에서 제거, 새 사진은 팔레트로
  function onPickerConfirm(newSelected: SelectedPhoto[]) {
    setAllSelected(newSelected);
    const keep = new Set(newSelected.map((p) => p.photoId));
    setBlocks((bs) => {
      const filtered = bs
        .map((b) => (b.type === 'photo' ? { ...b, photos: (b.photos ?? []).filter((p) => keep.has(p.photoId)) } : b))
        .filter((b) => b.type !== 'photo' || (b.photos?.length ?? 0) > 0);
      return normalizeParagraphs(filtered, modeOf(preset));
    });
  }

  async function save(publish: boolean) {
    if (!title.trim()) return setError('제목을 입력하세요.');
    if (!locationText.trim()) return setError('위치는 필수입니다.');
    setError(null);
    setSaving(true);

    // 정규화: 사진 줄 columns=사진수, 편집전용 bid 제거, 빈 줄·빈 문단 제거
    const norm: Block[] = blocks
      .map((b) => {
        if (b.type !== 'photo') return b;
        const copy: Block = { ...b, columns: Math.max(1, b.photos?.length ?? 0) };
        delete copy.bid;
        return copy;
      })
      .filter((b) => (b.type === 'photo' ? (b.photos?.length ?? 0) > 0 : (b.text ?? '').trim().length > 0));
    const content: BlockDocument = { version: 1, preset, blocks: norm };
    const photos = blockPhotos(norm).map((p, i) => ({ photoId: p.photoId, cover: i === 0 }));
    // projectPyeong은 DB 타입 변경(String→Integer)으로 숫자만 허용 — 입력창은 자유 텍스트("24평" 등)를
    // 그대로 받으므로 숫자만 추출해 변환한다(비숫자 입력은 소실됨 — 필요 시 입력창을 숫자 전용으로 바꿔야 한다).
    const pyeongDigits = pyeong.trim().replace(/[^0-9]/g, '');
    const body: ProjectUpsertRequest = {
      projectTitle: title.trim(),
      siteId: siteId,
      locationText: locationText.trim(),
      projectPyeong: pyeongDigits ? Number(pyeongDigits) : null,
      durationDays: durationDays.trim() ? Number(durationDays) : null,
      budgetRange: budgetRange.trim() || null,
      content,
      photos,
      tags: hashtags.map((h) => ({ tagType: 'HASH', tagValue: h })),
    };
    try {
      let id = projectId;
      if (id == null) {
        const res = await portfolioApi.createProject(body);
        id = res.data.data;
      } else {
        await portfolioApi.updateProject(id, body);
      }
      if (publish && id != null) await portfolioApi.publish(id);
      bypassBlockRef.current = true; // 저장 완료 후 이동은 가드 통과
      navigate('/agents/portfolio/projects');
    } catch {
      setError('저장에 실패했습니다.');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Page layout="doc" width="xl">
        <p style={{ color: 'var(--shell-on-surface-2)' }}>불러오는 중…</p>
      </Page>
    );
  }

  // ── 신규 진입: 전체화면 사진 고르기 ──
  if (view === 'select') {
    return (
      // 사진 고르기 단계 — 문서형(doc). 폭 900(스케일 밖) → lg(1060). 액션은 하단 sticky 바가 갖는다
      <Page layout="doc" width="lg">
        <PageHeader title="어떤 현장의 사진으로 시작할까요?" />

        <div style={{ marginTop: 16, maxWidth: 360 }}>
          <Select
            label="현장"
            value={siteId ?? ''}
            onChange={(e) => onSelectSite(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">현장을 선택하세요</option>
            {sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {(s.bldgName || s.siteName) + (s.siteAddress ? ` · ${s.siteAddress}` : '')}
              </option>
            ))}
          </Select>
          {sitesLoaded && sites.length === 0 && (
            <StatusHint tone="info" className="mt-03">
              담당·관리하는 현장이 없어요. 현장 담당자 또는 관리자로 지정되면 여기에 보여요.
            </StatusHint>
          )}
        </div>

        {/* 선택 현장 피커 — 처음부터 노출(현장 미선택 시 빈 그리드). 기간 + 태그(다중, 그 현장 사진들의 태그) */}
        <div style={{ marginTop: 16 }}>
          <PhotoSelectGrid
            selected={[...selMap.values()]}
            onToggle={toggleSel}
            siteId={siteId}
            requireSite
            showKeyword={false}
            showTags
            showStrip={false}
            maxHeight={440}
          />
        </div>

        {/* 다른 현장의 사진도 포함 — 접이식(기본 접힘) */}
        <div style={{ marginTop: 16 }}>
          <CollapsibleSection
            title="다른 현장의 사진도 포함하시나요?"
            open={otherOpen}
            onToggle={() => setOtherOpen((v) => !v)}
          >
            <PhotoSelectGrid
              selected={[...selMap.values()]}
              onToggle={toggleSel}
              showKeyword
              manual
              showStrip={false}
              maxHeight={360}
            />
          </CollapsibleSection>
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '14px 0',
            marginTop: 8,
            background: 'var(--shell-surface)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--shell-on-surface-2)', fontSize: 13 }}>{selMap.size}장 선택됨</span>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button variant="etc" onClick={() => navigate('/agents/portfolio/projects')}>
                취소
              </Button>
              <Button variant="primary" onClick={startCanvas}>
                이 사진들로 만들기
              </Button>
            </div>
          </div>
          <SelectedStrip selected={[...selMap.values()]} onRemove={toggleSel} />
        </div>
      </Page>
    );
  }

  // ── 구성(canvas) 뷰 — 3분할 ──
  // 사진 블록 인덱스 목록.
  const photoIdxs = blocks.reduce<number[]>((acc, b, i) => {
    if (b.type === 'photo') acc.push(i);
    return acc;
  }, []);
  const photoCount = photoIdxs.length;
  const trailingTextIdx = blocks.findIndex(isText);

  // 좌측 문단 표시기 목록.
  type OutlineItem =
    | { kind: 'pair'; k: number; bid: number; columns: number; preview: string }
    | { kind: 'photo'; k: number; bid: number; columns: number }
    | { kind: 'text'; preview: string };
  const outline: OutlineItem[] =
    textMode === 'trailing'
      ? [
          ...photoIdxs.map((pi, k): OutlineItem => ({ kind: 'photo', k, bid: blocks[pi].bid ?? k + 1, columns: blocks[pi].columns ?? 1 })),
          ...(trailingTextIdx >= 0
            ? [{ kind: 'text' as const, preview: previewText(blocks[trailingTextIdx].text) }]
            : []),
        ]
      : photoIdxs.map((pi, k): OutlineItem => ({
          kind: 'pair',
          k,
          bid: blocks[pi].bid ?? k + 1,
          columns: blocks[pi].columns ?? 1,
          preview: previewText(blocks[pi + 1]?.type === 'text' ? blocks[pi + 1].text : ''),
        }));

  const thS: React.CSSProperties = {
    textAlign: 'left',
    color: 'var(--shell-on-surface-2)',
    fontWeight: 600,
    width: 64,
    padding: '3px 8px 3px 0',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
  };
  const tdS: React.CSSProperties = { padding: '3px 0' };

  return (
    // D-07 §21.5 L4 풀높이 에디터 — 액션 푸터를 고정하고 PageScroll 이 스크롤·폭·패딩을 갖는다(§21.4).
    // 메타 영역이 폼(제목·위치·평수 등)이라 "메타행 고정"으로는 편집 영역이 남지 않아 본문 전체를 스크롤에 둔다.
    <Page layout="fixed">
      <PageScroll width="xl">
        <PageHeader title={projectId == null ? '시공사례 작성' : '시공사례 수정'} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-md)' }}>
        {/* 상단: 회사정보 (접이식·기본 접힘·읽기 전용) */}
        <CollapsibleSection
          title="회사정보"
          open={companyOpen}
          onToggle={() => setCompanyOpen((v) => !v)}
          disabled={!company}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <tbody>
              {company?.compName && (<tr><th style={thS}>상호</th><td style={tdS}>{company.compName}</td></tr>)}
              {company?.compRepName && (<tr><th style={thS}>대표</th><td style={tdS}>{company.compRepName}</td></tr>)}
              {company?.phone && (<tr><th style={thS}>연락처</th><td style={tdS}>{company.phone}</td></tr>)}
              {company?.email && (<tr><th style={thS}>이메일</th><td style={tdS}>{company.email}</td></tr>)}
              {company?.address && (<tr><th style={thS}>주소</th><td style={tdS}>{company.address}</td></tr>)}
            </tbody>
          </table>
        </CollapsibleSection>

        {/* 해시태그 (접이식·기본 펼침·자동채움·편집) */}
        <CollapsibleSection title="해시태그" open={hashtagOpen} onToggle={() => setHashtagOpen((v) => !v)}>
          <KeywordForm
            tags={hashtags.map((h) => `#${h}`)}
            normalize={(v) => `#${v.replace(/^#+/, '')}`}
            onAdd={(v) => setHashtags((hs) => [...hs, v.slice(1)])}
            onRemove={(i) => setHashtags((hs) => hs.filter((_, j) => j !== i))}
            placeholder="태그 입력 후 엔터"
          />
          <div style={{ marginTop: 8 }}>
            <StatusHint tone="info" icon="ℹ">
              발행 시 첫 사진 위와 블로그 복사에 사용됩니다.
            </StatusHint>
          </div>
        </CollapsibleSection>

        {/* 기본정보 */}
        <Input label="제목" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="예: 모던 화이트 리모델링" width="fill" />
        <div>
          <Input
            label="위치"
            required
            value={locationText}
            onChange={(e) => setLocationText(e.target.value)}
            placeholder="예: 행복마을 3단지"
            width="fill"
          />
          <div style={{ marginTop: 6 }}>
            <StatusHint tone="attention" icon="⚠">
              고객의 개인정보 유출방지를 위해 상세주소는 입력하지 마세요
            </StatusHint>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Input label="평수" value={pyeong} onChange={(e) => setPyeong(e.target.value)} placeholder="24평" />
          <Input label="시공기간(일)" type="number" value={durationDays} onChange={(e) => setDurationDays(e.target.value)} placeholder="18" />
          <Input label="예산대" value={budgetRange} onChange={(e) => setBudgetRange(e.target.value)} placeholder="2000만원대" />
        </div>

        {/* 본문: 좌 문단표시기 / 중 캔버스 / 우 팔레트 */}
        <div style={{ borderTop: '1px solid var(--shell-border)', paddingTop: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ fontSize: 14 }}>스타일</strong>
                <Segmented options={PRESETS} value={preset} onChange={applyPreset} />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="스타일 예시 보기"
                  title="스타일 예시 보기"
                  onClick={() => setHelpOpen(true)}
                  className="!h-auto shrink-0 !p-0.5 !text-text-muted hover:!text-group"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 2-3 4" /><line x1="12" y1="17.5" x2="12" y2="17.51" />
                  </svg>
                </Button>
                <span
                  onClick={() => setHelpOpen(true)}
                  style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', cursor: 'pointer' }}
                >
                  (스타일 예시)
                </span>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                사진 추가·삭제
              </Button>
            </div>
            <div style={{ marginTop: 8 }}>
              <StatusHint tone="info" icon="ℹ">
                스타일을 바꾸면 사진이 다시 묶이고, 캡션은 지워지며, 입력한 설명은 하나로 합쳐집니다.
              </StatusHint>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            {/* 좌: 페이지 구성 (블록 목록 + 드래그 순서) — 스크롤 따라오도록 sticky */}
            <div style={{ width: 214, flexShrink: 0, position: 'sticky', top: 'var(--gap-md)', maxHeight: STICKY_COL_MAX, overflowY: 'auto' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 8 }}>
                페이지 구성
              </div>
              {outline.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>사진을 배치하면 목록이 표시됩니다.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {outline.map((it, idx) => {
                    const isTextItem = it.kind === 'text';
                    const k = it.kind === 'text' ? -1 : it.k;
                    const primary =
                      it.kind === 'text' ? '설명글' : `${UNIT_LABEL} ${it.bid}  |  사진수량 : ${it.columns}`;
                    const sub = it.kind === 'pair' || it.kind === 'text' ? it.preview : '';
                    return (
                      <div
                        key={idx}
                        draggable={!isTextItem}
                        onDragStart={isTextItem ? undefined : () => setDragUnitIdx(k)}
                        onDragEnd={isTextItem ? undefined : () => setDragUnitIdx(null)}
                        onDragOver={
                          isTextItem
                            ? undefined
                            : (e) => {
                                if (dragUnitIdx != null && dragUnitIdx !== k) {
                                  e.preventDefault();
                                  reorderUnit(dragUnitIdx, k);
                                  setDragUnitIdx(k);
                                }
                              }
                        }
                        onDrop={isTextItem ? undefined : () => setDragUnitIdx(null)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '7px 9px',
                          border: '1px solid var(--shell-border)',
                          borderRadius: 8,
                          background: 'var(--shell-surface)',
                          cursor: isTextItem ? 'default' : 'grab',
                          opacity: !isTextItem && dragUnitIdx === k ? 0.5 : 1,
                        }}
                      >
                        {!isTextItem && (
                          <span aria-hidden="true" style={{ color: 'var(--shell-on-surface-2)', fontSize: 13, flexShrink: 0 }}>
                            ⠿
                          </span>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 700 }}>{primary}</div>
                          {sub && (
                            <div
                              style={{
                                fontSize: 'var(--type-caption-size)',
                                color: 'var(--shell-on-surface-2)',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {sub}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 중: WYSIWYG 캔버스 (발행 페이지에 가까운 흰 지면) */}
            <div
              style={{
                flex: 1,
                background: 'var(--shell-surface)',
                border: '1px solid var(--shell-border)',
                borderRadius: 12,
                padding: '22px 26px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 0,
              }}
            >
              {photoCount === 0 && (
                <p style={{ color: 'var(--shell-on-surface-2)', fontSize: 13 }}>사진을 배치해 구성하세요.</p>
              )}
              {photoIdxs.map((pi, k) => {
                const photo = blocks[pi];
                const textIdx = textMode === 'perRow' && blocks[pi + 1]?.type === 'text' ? pi + 1 : -1;
                return (
                  <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <CanvasPhotoRow
                      photos={(photo.photos ?? []).map((p) => ({ id: p.photoId, url: p.url }))}
                      columns={photo.columns ?? 1}
                      caption={photo.caption}
                      onColumns={(d) => changeColumns(pi, d)}
                      onRemovePhoto={(pid) => removePhotoFromRow(pi, Number(pid))}
                      onDropPhoto={() => {
                        if (dragPhotoId != null) addPhotoToRow(pi, dragPhotoId);
                        setDragPhotoId(null);
                      }}
                      onCaption={(v) => setCaption(pi, v)}
                      onRemoveRow={() => removeRow(pi)}
                      renderPhoto={(ph) => <ThumbImg url={ph.url} />}
                    />
                    {textIdx >= 0 && (
                      <CanvasTextRow value={blocks[textIdx].text ?? ''} onChange={(v) => setText(textIdx, v)} />
                    )}
                  </div>
                );
              })}
              {/* 갤러리: 사진들 뒤 문단 1개 */}
              {textMode === 'trailing' && trailingTextIdx >= 0 && (
                <CanvasTextRow
                  value={blocks[trailingTextIdx].text ?? ''}
                  onChange={(v) => setText(trailingTextIdx, v)}
                  placeholder="사진 아래에 들어갈 설명을 입력하세요"
                />
              )}
            </div>

            {/* 우: 팔레트 (미배치 사진) — 스크롤 따라오도록 sticky */}
            <div
              style={{
                width: 176,
                flexShrink: 0,
                position: 'sticky',
                top: 'var(--gap-md)',
                maxHeight: STICKY_COL_MAX,
                overflowY: 'auto',
                background: 'var(--shell-surface)',
                border: '1px solid var(--shell-border)',
                borderRadius: 12,
                padding: '14px 12px',
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--shell-on-surface-2)', marginBottom: 8 }}>
                안 쓴 사진 {palette.length}
              </div>
              {palette.length === 0 ? (
                <p style={{ fontSize: 12, color: 'var(--shell-on-surface-2)' }}>모든 사진이 배치됐어요.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {palette.map((p) => {
                    const isOther = siteId != null && p.siteId != null && p.siteId !== siteId && !!p.siteName;
                    return (
                      <div
                        key={p.photoId}
                        draggable
                        onDragStart={() => setDragPhotoId(p.photoId)}
                        onDragEnd={() => setDragPhotoId(null)}
                        style={{ position: 'relative', width: '100%', aspectRatio: '4 / 3', borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'grab', background: 'var(--shell-bg)' }}
                      >
                        <ThumbImg url={p.url} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        {/* 타 현장 사진 표시 — 사진 위 오버레이 칩 규격은 PhotoCard(§15.x)와 동일 계열 */}
                        {isOther && (
                          <span
                            style={{
                              position: 'absolute',
                              left: 'var(--spacing-02)',
                              bottom: 'var(--spacing-02)',
                              maxWidth: 'calc(100% - var(--spacing-03))',
                              background: 'var(--alpha-black-700)',
                              color: 'var(--gray-0)',
                              fontSize: 'var(--type-caption-size)',
                              lineHeight: 'var(--type-caption-line)',
                              padding: 'var(--spacing-01) var(--spacing-02)',
                              borderRadius: 'var(--radius-sm)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {p.siteName}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <StatusHint tone="error" icon="⚠">
            {error}
          </StatusHint>
        )}

        </div>
      </PageScroll>

      {/* 하단 고정 액션 바 — 바는 전폭, 안쪽 콘텐츠는 본문 컬럼(xl)과 같은 좌우 기준선 */}
      <div style={{ flexShrink: 0, borderTop: 'var(--border-width-base) solid var(--shell-border)', background: 'var(--shell-surface)' }}>
        <div
          className="lay-w-xl"
          style={{ display: 'flex', gap: 'var(--gap-sm)', justifyContent: 'flex-end', padding: 'var(--spacing-04) var(--page-padding-desktop)' }}
        >
          {/* 취소는 뉴트럴(etc) — 옆의 임시저장(secondary)과 외형이 갈리게 둔다 */}
          <Button variant="etc" onClick={onCancel} disabled={saving}>
            취소
          </Button>
          <Button variant="secondary" onClick={() => save(false)} disabled={saving}>
            임시저장
          </Button>
          <Button variant="primary" onClick={() => save(true)} disabled={saving}>
            공개 발행
          </Button>
        </div>
      </div>

      <PhotoPicker open={pickerOpen} onClose={() => setPickerOpen(false)} initialSelected={allSelected} onConfirm={onPickerConfirm} />

      <Modal open={helpOpen} onClose={() => setHelpOpen(false)} title="배치 스타일 미리보기" size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* 범례 — 회색 바 = 문단 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--shell-on-surface-2)' }}>
            <span
              style={{
                width: 16,
                height: 12,
                borderRadius: 3,
                background: 'var(--shell-bg)',
                border: '1px solid var(--shell-border)',
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            회색 줄은 문단(설명 글)이 들어가는 자리입니다.
          </div>

          {PRESET_EXAMPLES.map((ex) => (
            <div key={ex.label} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <img
                src={ex.img}
                alt={`${ex.label} 배치 예시`}
                width={132}
                style={{
                  width: 132,
                  height: 'auto',
                  borderRadius: 10,
                  border: '1px solid var(--shell-border)',
                  background: 'var(--shell-surface)',
                  flexShrink: 0,
                }}
              />
              <div style={{ minWidth: 0 }}>
                <strong style={{ fontSize: 14 }}>{ex.label}</strong>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }}>{ex.desc}</p>
              </div>
            </div>
          ))}

          <p
            style={{
              margin: 0,
              display: 'flex',
              gap: 6,
              fontSize: 12,
              lineHeight: 1.6,
              color: 'var(--shell-on-surface-2)',
              borderTop: '1px solid var(--shell-border)',
              paddingTop: 12,
            }}
          >
            <span aria-hidden="true" style={{ flexShrink: 0, color: 'var(--color-info)' }}>ℹ</span>
            <span>어떤 스타일을 고르든, 이후 각 사진 줄의 열(사진 수)은 언제든지 추가·삭제할 수 있습니다.</span>
          </p>
        </div>
      </Modal>
    </Page>
  );
}
